import { RECURRING } from 'self-service-api/constants/ChargeTypes';
import { SOFTWARE } from 'self-service-api/constants/ProductTypes';
import { QUANTITY_PACK } from 'self-service-api/constants/SkuTypes';
import { ANNUAL_10_DISCOUNT, MONTHLY_NO_DISCOUNT } from 'self-service-api/constants/TermIds';
import { ANNUAL, MONTHLY, ONCE } from 'self-service-api/constants/Terms';
import { getIsUngatedForSeats } from './gatingUtils';
import { CRM_SUITE_PRODUCTS, getCrmSuiteProductLevelForProductConfigurations, getCrmSuiteProductLevelForSkuIds, getHubProductVersionSkuIdsOrDefault, getIsCrmSuiteSalesOrServiceSkuProEnt, getProductForSkuId, getProductVersion, getSkuIdIsAddonOrService, getSkuIdsContainOnlyCRMSuite } from './productsUtils';
import { getOffersMap, getProductPromotion, getPromotionForSkuId } from './promotionUtils';
import Raven from 'Raven';
/**
 * Sort offers such that lower terms come first
 * at time of writing, this means MCMP, ACMP, ACAP
 */

export const offerInfoSortingFunc = (firstInfo, secondInfo) => {
  if (!firstInfo || !secondInfo) return 0;

  if (firstInfo.payment === MONTHLY && secondInfo.payment === ANNUAL || firstInfo.commitment === MONTHLY && secondInfo.commitment === ANNUAL) {
    return -1;
  }

  return 1;
};
export const sortQuantityPackOfferIdsForUnitType = (offersById, quantityPackIdsForUnitType) => {
  if (Array.isArray(quantityPackIdsForUnitType)) {
    return quantityPackIdsForUnitType.sort((firstOfferId, secondOfferId) => {
      return offerInfoSortingFunc(offersById[firstOfferId], offersById[secondOfferId]);
    });
  } else {
    const sortedQuantityPackOfferIds = Object.keys(quantityPackIdsForUnitType).reduce((acc, unitScope) => {
      const promotionObjectForUnitScope = quantityPackIdsForUnitType[unitScope];
      const {
        offerIds
      } = promotionObjectForUnitScope;
      const sortedQuantityPackOfferIdsForUnitScope = offerIds.sort((firstOfferId, secondOfferId) => {
        return offerInfoSortingFunc(offersById[firstOfferId], offersById[secondOfferId]);
      });
      return Object.assign({}, acc, {
        [unitScope]: sortedQuantityPackOfferIdsForUnitScope
      });
    }, {});
    return sortedQuantityPackOfferIds;
  }
};
const reportedOfferInfoErrors = [];
export const getOfferInfo = ({
  promotions
}, {
  offerId
}) => {
  if (!offerId || !promotions || !promotions.offersById) return null;

  if (!(offerId in promotions.offersById)) {
    if (!reportedOfferInfoErrors.includes(offerId)) {
      reportedOfferInfoErrors.push(offerId);
      console.error('Called getOfferInfo with unknown offer id', {
        offerId,
        offersMap: promotions.offersById
      });
      throw new Error(`Called getOfferInfo with unknown offer id ${offerId}`);
    }
  }

  return promotions.offersById[offerId];
};
export const getMultipleOfferInfo = (state, {
  offerIds
}) => {
  return offerIds.map(offerId => getOfferInfo(state, {
    offerId
  })).filter(offerInfo => offerInfo).sort(offerInfoSortingFunc);
};
export const getAvailableOfferIdsForPromotion = ({
  promotions
}, {
  promotion,
  limitToTwo = true
}) => {
  const {
    offerIds
  } = promotion;
  const newOfferIds = [...offerIds];

  if (limitToTwo && offerIds && offerIds.length > 2) {
    newOfferIds.splice(2, 1);
  }

  return (newOfferIds || []).sort((firstOfferId, secondOfferId) => {
    return offerInfoSortingFunc(promotions.offersById[firstOfferId], promotions.offersById[secondOfferId]);
  });
};
export const findOfferIdForTerm = ({
  promotions
}, {
  offerIds,
  offerTerms
}) => {
  if (Array.isArray(offerIds)) {
    return offerIds.find(offerId => {
      const offerInfo = getOfferInfo({
        promotions
      }, {
        offerId
      });
      return offerInfo && 'commitment' in offerTerms && 'payment' in offerTerms && offerInfo.commitment === offerTerms.commitment && offerInfo.payment === offerTerms.payment;
    });
  } else {
    const offerTermsForEveryUnitScope = Object.keys(offerIds).every(item => item in offerTerms);
    const offerIdByUnitScope = offerTermsForEveryUnitScope ? Object.keys(offerIds).reduce((acc, unitScope) => {
      const offerIdsForUnitScope = offerIds[unitScope];
      const offerTermsForUnitScope = offerTerms[unitScope];
      const offerIdForTerm = offerIdsForUnitScope.find(offerId => {
        const offerInfo = getOfferInfo({
          promotions
        }, {
          offerId
        });
        return offerInfo && offerInfo.commitment === offerTermsForUnitScope.commitment && offerInfo.payment === offerTermsForUnitScope.payment;
      });
      return Object.assign({}, acc, {
        [unitScope]: offerIdForTerm
      });
    }, {}) : undefined;
    return offerIdByUnitScope;
  }
};
export const getAvailableOfferIdsForSku = (state, {
  skuId,
  limitToTwo
}) => {
  const promotion = getPromotionForSkuId(state, {
    skuId
  });
  return getAvailableOfferIdsForPromotion(state, {
    promotion,
    limitToTwo
  });
};
export const getProductBasedOfferId = (state, {
  skuId
}) => {
  const promotion = getPromotionForSkuId(state, {
    skuId
  });
  const offerIds = getAvailableOfferIdsForPromotion(state, {
    promotion,
    limitToTwo: false
  });
  return offerIds.find(offerId => {
    const offerInfo = getOfferInfo(state, {
      offerId
    });
    return offerInfo && offerInfo.payment === ONCE;
  });
}; // Returns a list of terms available for a sku
// limitToTwoTerms will remove ACMP if all three terms are valid
// (which can happen for add-ons)

export const getAvailableTermsForSku = (state, {
  skuId,
  limitToTwo
}) => {
  return getMultipleOfferInfo(state, {
    offerIds: getAvailableOfferIdsForSku(state, {
      skuId,
      limitToTwo
    })
  });
};
export const getProductVersionDoesntAllowDiscount = (state, {
  skuId
}) => {
  const product = getProductForSkuId(state, {
    skuId
  });
  if (!product) return null;
  const {
    chargeType
  } = product;
  const isRecurringMerch = chargeType === RECURRING;
  return !isRecurringMerch;
}; // Takes in a SKU id, terms object, and optionally fallback terms
// in case the requested term isn't available.
// Returns the offer id

export const getOfferId = (state, {
  skuId,
  terms,
  fallbackTerms = []
}) => {
  if (getProductVersionDoesntAllowDiscount(state, {
    skuId
  })) {
    return getProductBasedOfferId(state, {
      skuId
    }) || MONTHLY_NO_DISCOUNT;
  }

  const {
    offerIds
  } = getPromotionForSkuId(state, {
    skuId
  });
  const offersMap = getOffersMap(state);
  if (!offerIds || !offersMap || !terms) return null;
  return [terms, ...fallbackTerms].map(term => findOfferIdForTerm(state, {
    offerIds,
    offerTerms: term
  })).find(offerId => offerId) || null;
};
export const getCrmSuiteOfferIdsForProductLevel = (state, {
  productLevel
}) => {
  const {
    promotions
  } = state;
  const {
    crmSuiteOfferIds: {
      [productLevel]: {
        productOfferIds,
        quantityPackOfferIds
      }
    }
  } = promotions;
  return {
    productOfferIds,
    quantityPackOfferIds
  };
};
export const getCrmSuiteProductLevelForOfferId = (state, {
  offerId
}) => {
  if (!offerId) return null;
  const suiteProductLevels = Object.keys(CRM_SUITE_PRODUCTS);
  const foundProductLevel = suiteProductLevels.find(productLevel => {
    const {
      productOfferIds
    } = getCrmSuiteOfferIdsForProductLevel(state, {
      productLevel
    });
    return productOfferIds.includes(offerId);
  });
  return foundProductLevel || null;
};
export const getCrmSuiteQuantityPackOfferIdsForProductLevel = (state, {
  quantityPackSkuId,
  productLevel,
  unitType
}) => {
  const {
    apiName
  } = getProductForSkuId(state, {
    skuId: quantityPackSkuId
  });
  const {
    quantityPackOfferIds
  } = getCrmSuiteOfferIdsForProductLevel(state, {
    productLevel
  });

  if (!Object.keys(quantityPackOfferIds).includes(apiName)) {
    return [];
  }

  const {
    [apiName]: quantityPackOfferIdsForSuiteProduct
  } = quantityPackOfferIds;

  if (!Object.keys(quantityPackOfferIdsForSuiteProduct).length || !Object.keys(quantityPackOfferIdsForSuiteProduct).includes(unitType)) {
    return [];
  }

  const {
    [unitType]: quantityPackUnitOfferIds
  } = quantityPackOfferIdsForSuiteProduct;
  return quantityPackUnitOfferIds;
};
export const getCrmSuiteAvailableTerms = (state, {
  productLevel
}) => {
  const {
    productOfferIds
  } = getCrmSuiteOfferIdsForProductLevel(state, {
    productLevel
  });
  return getMultipleOfferInfo(state, {
    offerIds: productOfferIds
  });
};
export const getCrmSuiteOfferId = (state, {
  productLevel,
  terms = {
    commitment: ANNUAL,
    payment: ANNUAL
  },
  fallbackTerms = []
}) => {
  const {
    productOfferIds
  } = getCrmSuiteOfferIdsForProductLevel(state, {
    productLevel
  });
  return [terms, ...fallbackTerms].map(term => findOfferIdForTerm(state, {
    offerIds: productOfferIds,
    offerTerms: term
  })).find(offerId => offerId) || null;
};
export const getCrmSuiteQuantityPackOfferId = (state, {
  quantityPackSkuId,
  productLevel,
  unitType,
  terms = {
    commitment: ANNUAL,
    payment: ANNUAL
  },
  fallbackTerms = []
}) => {
  const quantityPackOfferIds = getCrmSuiteQuantityPackOfferIdsForProductLevel(state, {
    quantityPackSkuId,
    productLevel,
    unitType
  });
  return [terms, ...fallbackTerms].map(term => findOfferIdForTerm(state, {
    offerIds: quantityPackOfferIds,
    offerTerms: term
  })).find(offerId => offerId) || null;
};
export const getCrmSuiteLowestTermsOfferId = (state, {
  productLevel
}) => {
  const terms = {
    commitment: MONTHLY,
    payment: MONTHLY
  };
  const fallbackTerms = [{
    commitment: ANNUAL,
    payment: MONTHLY
  }, {
    commitment: ANNUAL,
    payment: ANNUAL
  }];

  if (!productLevel) {
    console.error('Invalid productLevel passed to getCrmSuiteLowestTermsOfferId', {
      productLevel
    });
    return null;
  }

  return getCrmSuiteOfferId(state, {
    productLevel,
    terms,
    fallbackTerms
  });
};
export const getCrmSuiteHighestTermsOfferId = (state, {
  productLevel
}) => {
  const terms = {
    commitment: ANNUAL,
    payment: ANNUAL
  };
  const fallbackTerms = [{
    commitment: ANNUAL,
    payment: MONTHLY
  }, {
    commitment: MONTHLY,
    payment: MONTHLY
  }];

  if (!productLevel) {
    console.error('Invalid productLevel passed to getCrmSuiteHighestTermsOfferId', {
      productLevel
    });
    return null;
  }

  return getCrmSuiteOfferId(state, {
    productLevel,
    terms,
    fallbackTerms
  });
};
export const getCrmSuiteQuantityPackLowestTermsOfferId = (state, {
  quantityPackSkuId,
  productLevel,
  unitType
}) => {
  const terms = {
    commitment: MONTHLY,
    payment: MONTHLY
  };
  const fallbackTerms = [{
    commitment: ANNUAL,
    payment: MONTHLY
  }, {
    commitment: ANNUAL,
    payment: ANNUAL
  }];

  if (!productLevel) {
    console.error('Invalid productLevel passed to getCrmSuiteQuantityPackLowestTermsOfferId', {
      productLevel
    });
    return null;
  }

  return getCrmSuiteQuantityPackOfferId(state, {
    quantityPackSkuId,
    productLevel,
    unitType,
    terms,
    fallbackTerms
  });
}; // If the query param is annual, always pick the second lowest terms offer id
// (low in this context means lower commitment and payment terms)
// Otherwise, pick the first available offer id

const offerIdForQueryParamTerm = (offerIds, queryParamTerm) => {
  if (Array.isArray(offerIds)) {
    if (queryParamTerm === ANNUAL && offerIds.length > 1) {
      return offerIds[1];
    } else if (offerIds.length) {
      return offerIds[0];
    }

    return null;
  } else {
    const offerIdByUnitScope = Object.keys(offerIds).reduce((acc, unitScope) => {
      const offerIdsForUnitScope = offerIds[unitScope];
      let offerIdForUnitScope = null;

      if (queryParamTerm === ANNUAL && offerIdsForUnitScope.length > 1) {
        offerIdForUnitScope = offerIdsForUnitScope[1];
      } else if (offerIdsForUnitScope.length) {
        offerIdForUnitScope = offerIdsForUnitScope[0];
      }

      return Object.assign({}, acc, {
        [unitScope]: offerIdForUnitScope
      });
    }, {});
    return offerIdByUnitScope;
  }
};

export const getOfferIdForQueryParamTerm = (state, {
  queryParamTerm,
  skuId
}) => {
  return offerIdForQueryParamTerm(getAvailableOfferIdsForSku(state, {
    skuId,
    limitToTwo: true
  }), queryParamTerm);
};
export const getQueryParamTermForOfferId = (state, {
  skuId,
  offerId
}) => {
  const offerIds = getAvailableOfferIdsForSku(state, {
    skuId,
    limitToTwo: true
  });
  return offerIds.length > 1 && offerIds[1] === offerId ? ANNUAL : MONTHLY;
};
export const getCrmSuiteOfferIdFromQueryParam = (state, {
  productLevel,
  queryParamTerm
}) => {
  const {
    productOfferIds
  } = getCrmSuiteOfferIdsForProductLevel(state, {
    productLevel
  });
  return offerIdForQueryParamTerm(productOfferIds, queryParamTerm);
};
export const getCrmSuiteQuantityPackOfferIdFromQueryParam = (state, {
  quantityPackSkuId,
  productLevel,
  queryParamTerm,
  unitType
}) => {
  const quantityPackOfferIds = getCrmSuiteQuantityPackOfferIdsForProductLevel(state, {
    quantityPackSkuId,
    productLevel,
    unitType
  });
  return offerIdForQueryParamTerm(quantityPackOfferIds, queryParamTerm);
};
export const getProductQuantityPackOfferIds = (state, {
  apiName,
  versionNumber,
  unitType
}) => {
  const {
    promotions
  } = state;
  const {
    offersById
  } = promotions;
  const {
    quantityPackOfferIds
  } = getProductPromotion(state, {
    apiName,
    versionNumber
  });
  const quantityPackOfferIdsForUnitType = quantityPackOfferIds && quantityPackOfferIds[unitType] || [];
  const sortedQuantityPackOfferIds = sortQuantityPackOfferIdsForUnitType(offersById, quantityPackOfferIdsForUnitType);
  return sortedQuantityPackOfferIds;
};
export const getProductQuantityPackOfferIdFromQueryParam = (state, {
  apiName,
  versionNumber,
  unitType,
  queryParamTerm
}) => {
  const quantityPackOfferIds = getProductQuantityPackOfferIds(state, {
    apiName,
    versionNumber,
    unitType
  });
  return offerIdForQueryParamTerm(quantityPackOfferIds, queryParamTerm);
};
export const getProductQuantityPackOfferId = (state, {
  apiName,
  versionNumber,
  unitType,
  terms = {
    commitment: ANNUAL,
    payment: ANNUAL
  },
  fallbackTerms = []
}) => {
  const quantityPackOfferIds = getProductQuantityPackOfferIds(state, {
    apiName,
    versionNumber,
    unitType
  });
  return [terms, ...fallbackTerms].map(term => findOfferIdForTerm(state, {
    offerIds: quantityPackOfferIds,
    offerTerms: term
  })).find(offerId => offerId) || null;
};
export const getProductQuantityPackLowestTermsOfferId = (state, {
  apiName,
  versionNumber,
  unitType
}) => {
  const terms = {
    commitment: MONTHLY,
    payment: MONTHLY
  };
  const fallbackTerms = [{
    commitment: ANNUAL,
    payment: MONTHLY
  }, {
    commitment: ANNUAL,
    payment: ANNUAL
  }];
  return getProductQuantityPackOfferId(state, {
    apiName,
    versionNumber,
    unitType,
    terms,
    fallbackTerms
  });
};

const validatedQueryParamTerm = ({
  queryParamTerm,
  offerTerms
}) => {
  if (!offerTerms || !offerTerms.length || offerTerms.some(terms => !terms)) {
    return null;
  }

  const isValid = offerTerms.some(terms => terms.commitment === queryParamTerm || terms.payment === queryParamTerm);

  if (isValid) {
    return queryParamTerm;
  }

  return queryParamTerm === MONTHLY ? ANNUAL : MONTHLY;
};

export const getValidatedQueryParamTermForSku = (state, {
  skuId,
  limitToTwo = true,
  queryParamTerm
}) => {
  const offerTerms = getAvailableTermsForSku(state, {
    skuId,
    limitToTwo
  });
  return validatedQueryParamTerm({
    queryParamTerm,
    offerTerms
  });
};
export const getValidatedQueryParamTermForCrmSuite = (state, {
  productLevel,
  queryParamTerm
}) => {
  const {
    promotions
  } = state;

  if (!Object.keys(promotions).length) {
    return null;
  }

  return validatedQueryParamTerm({
    offerTerms: getCrmSuiteAvailableTerms(state, {
      productLevel
    }),
    queryParamTerm
  });
};
export const getCommonAvailableOfferIds = (state, {
  skuIds
}) => {
  if (!skuIds) return [];
  const skuIdsToUse = getHubProductVersionSkuIdsOrDefault(state, {
    skuIds
  });
  const isOnlySuite = getSkuIdsContainOnlyCRMSuite(state, {
    skuIds
  });

  if (isOnlySuite) {
    const suiteProductLevel = getCrmSuiteProductLevelForSkuIds(state, {
      skuIds
    });

    if (suiteProductLevel) {
      const {
        productOfferIds
      } = getCrmSuiteOfferIdsForProductLevel(state, {
        productLevel: suiteProductLevel
      });
      return productOfferIds;
    }
  }

  const offerIdsList = skuIdsToUse.map(skuId => getAvailableOfferIdsForSku(state, {
    skuId,
    limitToTwo: false
  }));
  if (!offerIdsList.length) return [];
  return offerIdsList[0].filter(offerId => offerIdsList.every(offerIds => offerIds.includes(offerId)));
}; //Defaults to the lowest terms offer id that a list of merch has in common

export const getLowestCommonOfferId = (state, {
  skuIds,
  preferredTerm
}) => {
  const commonOfferIds = getCommonAvailableOfferIds(state, {
    skuIds
  });

  if (preferredTerm === ANNUAL && commonOfferIds.length > 1) {
    return commonOfferIds[1];
  } else if (commonOfferIds.length) {
    return commonOfferIds[0];
  }

  return null;
};
export const getCommonAvailableTerms = (state, {
  skuIds
}) => {
  if (!skuIds.length) return [];
  const isOnlySuite = getSkuIdsContainOnlyCRMSuite(state, {
    skuIds
  });
  const offerIdsList = skuIds.map(skuId => isOnlySuite ? getCommonAvailableOfferIds(state, {
    skuIds
  }) // will return the respective suite offer ids
  : getAvailableOfferIdsForSku(state, {
    skuId,
    limitToTwo: false
  }));
  const termsList = offerIdsList.map(offerIds => offerIds.map(offerId => getOfferInfo(state, {
    offerId
  })));
  const availableTerms = termsList[0].filter(term => termsList.every(terms => terms.some(termToCheck => term.payment === termToCheck.payment && term.commitment === termToCheck.commitment)));
  const isSeatsPilot = getIsUngatedForSeats(state);
  const monthlyNoOffer = {
    payment: MONTHLY,
    commitment: MONTHLY,
    discount: 0
  };
  const annual10Offer = {
    payment: ANNUAL,
    commitment: ANNUAL,
    discount: 10
  };
  const acmpNoOffer = {
    payment: MONTHLY,
    commitment: ANNUAL,
    discount: 0
  }; // TODO: Temporary workaround for Seats until we have promotions for Seat skus

  if (!availableTerms.length) {
    if (isSeatsPilot) {
      return [monthlyNoOffer, annual10Offer, acmpNoOffer];
    } else {
      Raven.captureMessage('Failed to find common available terms for skus', {
        extra: {
          skuIds
        }
      });
    }
  }

  return availableTerms;
};
export const getCommonPaymentTerms = (state, {
  skuIds
}) => {
  if (skuIds.length === 0) return null;
  const termMap = {
    [ONCE]: true,
    [MONTHLY]: true,
    [ANNUAL]: true
  };
  skuIds.filter(skuId => getProductForSkuId(state, {
    skuId
  }).productType === SOFTWARE).forEach(skuId => {
    const availableTerms = getAvailableTermsForSku(state, {
      skuId,
      limitToTwo: false
    });

    if (availableTerms && availableTerms.length) {
      Object.keys(termMap).forEach(term => {
        if (!availableTerms.some(({
          payment
        }) => payment === term)) termMap[term] = false;
      });
    }
  });
  return Object.keys(termMap).filter(term => termMap[term]);
};
/**
 * Finds lowest common payment terms for multiple skus, even if they
 * don't have a checkout offer id (used for showing cost to get started
 * for merch that isn't touchless)
 */

export const getLowestCommonPaymentTerm = (state, {
  skuIds
}) => {
  const commonPaymentTerms = getCommonPaymentTerms(state, {
    skuIds
  });
  return commonPaymentTerms && commonPaymentTerms.length ? commonPaymentTerms[0] : null;
};
export const getLowestTermsOfferId = (state, {
  skuId
}) => getOfferId(state, {
  skuId,
  terms: {
    commitment: MONTHLY,
    payment: MONTHLY
  },
  fallbackTerms: [{
    commitment: ANNUAL,
    payment: MONTHLY
  }, {
    commitment: ANNUAL,
    payment: ANNUAL
  }]
}); // Returns the offer id with the highest discount. Used to calculate the cheapest monthly cost
// of a sku

export const getHighestTermsOfferId = (state, {
  skuId
}) => getOfferId(state, {
  skuId,
  terms: {
    commitment: ANNUAL,
    payment: ANNUAL
  },
  fallbackTerms: [{
    commitment: ANNUAL,
    payment: MONTHLY
  }, {
    commitment: MONTHLY,
    payment: MONTHLY
  }]
}); // For CRM Suite Professional and Enterprise where we want to treat additional units, add ons, and
// and services with single product offer ids, rather than the suite offer id. This is temporary
// until our backend can support multiple offer ids/discounts.
// Slack thread for context: https://hubspot.slack.com/archives/C824F0ZB5/p1618244650087300

export const getMatchingTermsOfferId = (state, {
  currentOfferId,
  skuId
}) => {
  let offerInfoTerms;

  if (typeof currentOfferId === 'number') {
    const offerInfo = getOfferInfo(state, {
      offerId: currentOfferId
    });
    offerInfoTerms = offerInfo ? {
      payment: offerInfo.payment,
      commitment: offerInfo.commitment
    } : undefined;
  } else {
    offerInfoTerms = Object.keys(currentOfferId).reduce((acc, unitScope) => {
      const offerIdForUnitScope = currentOfferId[unitScope];
      const offerInfoForUnitScope = getOfferInfo(state, {
        offerId: offerIdForUnitScope
      });
      return Object.assign({}, acc, {
        [unitScope]: offerInfoForUnitScope ? {
          payment: offerInfoForUnitScope.payment,
          commitment: offerInfoForUnitScope.commitment
        } : undefined
      });
    }, {});
  }

  return getOfferId(state, {
    skuId,
    terms: offerInfoTerms,
    fallbackTerms: [{
      commitment: MONTHLY,
      payment: MONTHLY
    }, {
      commitment: ANNUAL,
      payment: ANNUAL
    }]
  });
};
/**
 * Determines the best offer id for a sku in a list of product configurations.
 */

export const getOfferIdForSkuInProductConfigurations = (state, {
  allProductConfigurations,
  productConfiguration,
  queryParamTerm
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  const suiteProductLevel = getCrmSuiteProductLevelForProductConfigurations(state, {
    productConfigurations: allProductConfigurations
  });
  const {
    skuType
  } = skuMap[productConfiguration.skuId];
  const productVersion = getProductVersion(state, {
    skuId: productConfiguration.skuId
  });
  if (!productVersion) throw new Error(`productVersion was undefined for skuId ${productConfiguration.skuId}`);
  const {
    skuId,
    versionNumber
  } = productVersion;
  const {
    apiName
  } = getProductForSkuId(state, {
    skuId
  });
  let offerIdToUse = null;
  const isSeatsPilot = getIsUngatedForSeats(state);

  if (getProductVersionDoesntAllowDiscount(state, {
    skuId
  })) {
    // Don't need to pass terms because this will either be a productBasedOffer or no discount
    offerIdToUse = getOfferId(state, {
      skuId
    });
  } else if (getSkuIdIsAddonOrService(state, {
    skuId
  })) {
    // For addons, we want to inherit the terms of the offer used for the hub skus.
    // This is because we cannot send multiple offer ids with different term configs to upgrade
    // catalog.
    // If no other hub sku is found, we can then assume that it is safe to use its own offer id.
    const skuIdToUseForAdditionalMerchDiscounts = allProductConfigurations.map(({
      skuId: skuIdInConfig
    }) => skuIdInConfig).find(skuIdInConfig => {
      const {
        productLine: productLineInConfig,
        productLevel: productLevelInConfig
      } = getProductForSkuId(state, {
        skuId: skuIdInConfig
      });
      return productLineInConfig && productLevelInConfig;
    });
    const currentOfferId = skuIdToUseForAdditionalMerchDiscounts && (suiteProductLevel ? queryParamTerm ? getCrmSuiteOfferIdFromQueryParam(state, {
      productLevel: suiteProductLevel,
      queryParamTerm
    }) : getCrmSuiteLowestTermsOfferId(state, {
      productLevel: suiteProductLevel
    }) : queryParamTerm ? getOfferIdForQueryParamTerm(state, {
      queryParamTerm,
      skuId: skuIdToUseForAdditionalMerchDiscounts
    }) : getLowestTermsOfferId(state, {
      skuId: skuIdToUseForAdditionalMerchDiscounts
    }));
    offerIdToUse = currentOfferId ? getMatchingTermsOfferId(state, {
      currentOfferId,
      skuId
    }) : queryParamTerm ? getOfferIdForQueryParamTerm(state, {
      skuId,
      queryParamTerm
    }) : getLowestTermsOfferId(state, {
      skuId
    });
  } else if (suiteProductLevel && // do not apply crm suite discount to sales/serivce pro/ent hub skus
  !(isSeatsPilot && getIsCrmSuiteSalesOrServiceSkuProEnt(state, {
    skuId,
    suiteProductLevel
  }))) {
    offerIdToUse = queryParamTerm ? getCrmSuiteOfferIdFromQueryParam(state, {
      productLevel: suiteProductLevel,
      queryParamTerm
    }) : getCrmSuiteLowestTermsOfferId(state, {
      productLevel: suiteProductLevel
    });

    if (skuType === QUANTITY_PACK) {
      const {
        quantityUnit: {
          unitType
        }
      } = skuMap[productConfiguration.skuId];
      offerIdToUse = queryParamTerm ? getCrmSuiteQuantityPackOfferIdFromQueryParam(state, {
        quantityPackSkuId: productConfiguration.skuId,
        productLevel: suiteProductLevel,
        queryParamTerm,
        unitType
      }) : getCrmSuiteQuantityPackLowestTermsOfferId(state, {
        quantityPackSkuId: productConfiguration.skuId,
        productLevel: suiteProductLevel,
        unitType
      });
    }
  } else {
    offerIdToUse = queryParamTerm ? getOfferIdForQueryParamTerm(state, {
      queryParamTerm,
      skuId
    }) : getLowestTermsOfferId(state, {
      skuId
    });

    if (skuType === QUANTITY_PACK) {
      const {
        quantityUnit: {
          unitType
        }
      } = skuMap[productConfiguration.skuId];
      offerIdToUse = queryParamTerm ? getProductQuantityPackOfferIdFromQueryParam(state, {
        apiName,
        versionNumber,
        unitType,
        queryParamTerm
      }) : getProductQuantityPackLowestTermsOfferId(state, {
        apiName,
        versionNumber,
        unitType
      });
    }
  }

  offerIdToUse = offerIdToUse || getLowestTermsOfferId(state, {
    skuId: productConfiguration.skuId
  }); // TODO: Temporary workaround for Seats until we have promotions for Seat skus

  if (!offerIdToUse) {
    if (isSeatsPilot) {
      if (queryParamTerm) {
        return queryParamTerm === ANNUAL ? ANNUAL_10_DISCOUNT : MONTHLY_NO_DISCOUNT;
      }

      return MONTHLY_NO_DISCOUNT;
    } else {
      Raven.captureMessage('Failed to find offer ID for sku', {
        extra: {
          skuId,
          queryParamTerm
        }
      });
    }
  }

  return offerIdToUse;
};
export const getMarketingHubEntIncreaseAnnouncementIsActive = (currentMilliseconds = Date.now()) => {
  const START_TIME = 1657771200000; // July 14, 2022, 00:00:00 EST

  const END_TIME = 1662004800000; // Sep 1, 2022, 00:00:00 EST

  return currentMilliseconds >= START_TIME && currentMilliseconds <= END_TIME;
};
export const getOfferIdFromIdByUnitScopeRecord = offerId => {
  if (!offerId) {
    return null;
  }

  if (typeof offerId === 'number') {
    return offerId;
  } else {
    return Object.values(offerId)[0];
  }
};